import { getCtx } from "./sound";

const BASE = { low: 110, mid: 220, high: 440 };

const hash = (s) => {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) h = Math.imul(h ^ s.charCodeAt(i), 16777619);
  return h >>> 0;
};

const osc = (c, out, type, t, dur, peak, attack, setup) => {
  const o = c.createOscillator();
  const g = c.createGain();
  o.type = type;
  g.gain.setValueAtTime(0.0001, t);
  g.gain.exponentialRampToValueAtTime(peak, t + attack);
  g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
  setup?.(o, g);
  o.connect(g).connect(out);
  o.start(t);
  o.stop(t + dur + 0.05);
};

const TEXTURES = {
  hum: (c, out, t, dur, f) => osc(c, out, "sine", t, dur, 0.18, dur * 0.35, (o) => {
    o.frequency.setValueAtTime(f, t);
    const lfo = c.createOscillator();
    const lg = c.createGain();
    lfo.frequency.value = 5.5;
    lg.gain.value = f * 0.015;
    lfo.connect(lg).connect(o.frequency);
    lfo.start(t);
    lfo.stop(t + dur + 0.05);
  }),
  click: (c, out, t, dur, f) => {
    const n = Math.max(2, Math.round(dur / 0.07));
    for (let i = 0; i < n; i++) {
      const st = t + i * 0.07;
      osc(c, out, "square", st, 0.035, 0.07, 0.002, (o) => o.frequency.setValueAtTime(f * 3, st));
    }
  },
  whistle: (c, out, t, dur, f) => osc(c, out, "sine", t, dur, 0.12, 0.05, (o) => {
    o.frequency.setValueAtTime(f * 2, t);
    o.frequency.exponentialRampToValueAtTime(f * 2.6, t + dur * 0.5);
    o.frequency.exponentialRampToValueAtTime(f * 1.9, t + dur);
  }),
  rumble: (c, out, t, dur, f) => {
    const filt = c.createBiquadFilter();
    filt.type = "lowpass";
    filt.frequency.value = 220;
    filt.connect(out);
    osc(c, filt, "sawtooth", t, dur, 0.3, 0.04, (o) => {
      o.frequency.setValueAtTime(f / 2, t);
      o.frequency.linearRampToValueAtTime(f / 2.4, t + dur);
    });
  },
  chirp: (c, out, t, dur, f) => osc(c, out, "triangle", t, dur * 0.7, 0.14, 0.01, (o) => {
    o.frequency.setValueAtTime(f, t);
    o.frequency.exponentialRampToValueAtTime(f * 2.4, t + dur * 0.5);
  }),
  bubble: (c, out, t, dur, f, seed) => {
    const n = Math.max(2, Math.round(dur / 0.09));
    for (let i = 0; i < n; i++) {
      const st = t + i * 0.09;
      const jitter = 1 + (((seed >> (i * 3)) & 7) / 7) * 0.8;
      osc(c, out, "sine", st, 0.08, 0.14, 0.005, (o) => {
        o.frequency.setValueAtTime(f * jitter, st);
        o.frequency.exponentialRampToValueAtTime(f * jitter * 1.8, st + 0.08);
      });
    }
  },
};

// Synthesises the colony's voice: one note per hyphenated fragment of the sound word.
export const playVoice = (voice = {}, soundWord = "", word = "") => {
  try {
    const c = getCtx();
    const out = c.createGain();
    out.gain.value = 0.9;
    out.connect(c.destination);
    const frags = (soundWord || word || "om").split(/[-\s]+/).filter(Boolean).slice(0, 5);
    const base = BASE[voice.pitch] || BASE.mid;
    const seed = hash(`${word}|${soundWord}`);
    const play = TEXTURES[voice.texture] || TEXTURES.hum;
    let t = c.currentTime + 0.03;
    frags.forEach((frag, i) => {
      const dur = 0.16 + Math.min(frag.length, 6) * 0.07;
      const f = base * (1 + (((seed >> (i * 4)) & 7) - 3) * 0.045);
      play(c, out, t, dur, f, seed);
      t += dur + 0.07;
    });
    return Math.round((t - c.currentTime) * 1000);
  } catch (e) {
    return 0;
  }
};
